import React, { useState, useRef, useEffect } from 'react';
import './ChipInput.scss';

export const ChipInput = ({
  value = [],
  onChange,
  placeholder = 'Add a skill and press Enter',
  maxChips = 20,
  maxChipLength = 50,
  disabled = false,
  className = '',
  allowDuplicates = false,
  error = '',
  focusVariant = '',
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  // Convert string value to array if needed (for backward compatibility)
  const chips = Array.isArray(value)
    ? value
    : typeof value === 'string' && value.trim()
    ? value
        .split(',')
        .map((chip) => chip.trim())
        .filter(Boolean)
    : [];

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addChip();
    } else if (e.key === 'Backspace' && inputValue === '' && chips.length > 0) {
      // Remove last chip when backspacing on empty input
      removeChip(chips.length - 1);
    }
  };

  const addChip = () => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) return;

    if (trimmedValue.length > maxChipLength) {
      return; // Could add error handling here
    }

    if (chips.length >= maxChips) {
      return; // Could add error handling here
    }

    if (
      !allowDuplicates &&
      chips.some((chip) => chip.toLowerCase() === trimmedValue.toLowerCase())
    ) {
      setInputValue('');
      return;
    }

    const newChips = [...chips, trimmedValue];
    onChange(newChips);
    setInputValue('');
  };

  const removeChip = (index) => {
    const newChips = chips.filter((_, i) => i !== index);
    onChange(newChips);
  };

  const handleContainerClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputBlur = () => {
    setFocused(false);
    // Add chip on blur if there's content
    if (inputValue.trim()) {
      addChip();
    }
  };

  const handleInputFocus = () => {
    setFocused(true);
  };

  return (
    <React.Fragment>
      <div
        className={`chip-input ${focused ? 'focused' : ''} ${
          disabled ? 'disabled' : ''
        } ${focusVariant ? `focus-${focusVariant}` : ''} ${className}`}
        onClick={handleContainerClick}
        {...props}
      >
        <div className='chip-input-content'>
          {chips.map((chip, index) => (
            <div key={index} className='chip'>
              <span className='chip-text'>{chip}</span>
              {!disabled && (
                <button
                  type='button'
                  className='chip-remove'
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChip(index);
                  }}
                  aria-label={`Remove ${chip}`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {!disabled && (
            <input
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={chips.length === 0 ? placeholder : ''}
              className='chip-input-field'
              disabled={disabled}
            />
          )}
        </div>
        {chips.length >= maxChips && (
          <div className='chip-input-limit'>
            Maximum {maxChips} skills allowed
          </div>
        )}
      </div>
      {error && <div className='chip-input-error'>{error}</div>}
    </React.Fragment>
  );
};
